package com.evoapps.evoshift.ui.roster

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.padding
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.OpenInNew
import androidx.compose.material.icons.filled.Print
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.di.diViewModel
import com.evoapps.evoshift.domain.model.DayRange
import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.ui.LightGold
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.viewmodels.RosterPrintViewModel
import com.evoapps.evoshift.viewmodels.defaultPrintWeek
import com.evoapps.evoshift.viewmodels.printWeeks
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.DialogWidth
import com.evoapps.evoshift.widgets.ErrorOnNavy
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.EvoDropdown
import com.evoapps.evoshift.widgets.EvoSegmented
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.cancel
import evoshift.composeapp.generated.resources.done
import evoshift.composeapp.generated.resources.print_action
import evoshift.composeapp.generated.resources.print_roster
import evoshift.composeapp.generated.resources.print_roster_draft_note
import evoshift.composeapp.generated.resources.print_roster_hint
import evoshift.composeapp.generated.resources.print_roster_one_week
import evoshift.composeapp.generated.resources.print_roster_open
import evoshift.composeapp.generated.resources.print_roster_ready
import evoshift.composeapp.generated.resources.print_roster_week
import evoshift.composeapp.generated.resources.print_roster_whole_month
import org.jetbrains.compose.resources.stringResource

/** "2.02–8.02" (one day: "2.02"). */
internal fun weekLabel(week: DayRange, month: Int): String {
    val mm = month.toString().padStart(2, '0')
    return if (week.fromDay == week.toDay) "${week.fromDay}.$mm" else "${week.fromDay}.$mm–${week.toDay}.$mm"
}

/**
 * "Print roster" (SH-261): the whole team's roster to pin up at work: the month on one A4 page
 * (as the e-mailed PDF, SH-207) or one week of it in larger print. Nobody's row is highlighted and
 * no absence is printed; open shifts are empty boxes still to fill, and a month not yet published
 * ([draft]) is marked so. The server makes the PDF (functions/rosterPrint.js) and the app opens it
 * (the system's viewer prints it); "Open" opens it again when the browser held it back.
 */
@Composable
fun PrintRosterDialog(
    rosterId: String,
    year: Int,
    month: Int,
    firstDayOfWeek: Int,
    draft: Boolean,
    onDismiss: () -> Unit
) {
    val viewModel: RosterPrintViewModel = diViewModel()
    val state by viewModel.state.collectAsState()
    LaunchedEffect(Unit) { viewModel.reset() }
    val weeks = remember(year, month, firstDayOfWeek) { printWeeks(year, month, firstDayOfWeek) }
    var wholeMonth by remember { mutableStateOf(true) }
    var week by remember(weeks) {
        mutableStateOf(defaultPrintWeek(weeks, year, month, AppClock.today()))
    }
    val uriHandler = LocalUriHandler.current
    LaunchedEffect(state.openUrl) {
        state.openUrl?.let {
            runCatching { uriHandler.openUri(it) }
            viewModel.urlOpened()
        }
    }
    val printed = state.printed != null

    EvoDialog(
        title = stringResource(Res.string.print_roster),
        icon = Icons.Default.Print,
        onDismiss = onDismiss,
        width = DialogWidth.Compact,
        dismissible = !state.busy,
        confirm = if (printed) {
            DialogAction(stringResource(Res.string.print_roster_open), { viewModel.open() }, icon = Icons.AutoMirrored.Filled.OpenInNew)
        } else {
            DialogAction(
                stringResource(Res.string.print_action),
                { viewModel.print(rosterId, week.takeIf { !wholeMonth }) },
                enabled = wholeMonth || week != null,
                loading = state.busy,
                icon = Icons.Default.Print
            )
        },
        dismiss = if (printed) DialogAction(stringResource(Res.string.done), onDismiss)
        else DialogAction(stringResource(Res.string.cancel), onDismiss, enabled = !state.busy),
        bodyArrangement = Arrangement.spacedBy(8.dp)
    ) {
        if (printed) {
            Text(stringResource(Res.string.print_roster_ready), color = Color.White, style = MaterialTheme.typography.bodyLarge)
        } else {
            Text(
                stringResource(Res.string.print_roster_hint),
                color = Color.White.copy(alpha = 0.7f),
                style = MaterialTheme.typography.bodySmall
            )
            val monthLabel = stringResource(Res.string.print_roster_whole_month)
            val weekOptionLabel = stringResource(Res.string.print_roster_one_week)
            EvoSegmented(
                options = listOf(true, false),
                selected = wholeMonth,
                onSelect = { wholeMonth = it },
                optionLabel = { if (it) monthLabel else weekOptionLabel }
            )
            if (!wholeMonth) {
                EvoDropdown(
                    label = stringResource(Res.string.print_roster_week),
                    options = weeks,
                    selected = week,
                    onSelected = { week = it },
                    optionLabel = { weekLabel(it, month) },
                    modifier = Modifier.padding(top = 4.dp)
                )
            }
            if (draft) {
                Text(
                    stringResource(Res.string.print_roster_draft_note),
                    color = LightGold,
                    style = MaterialTheme.typography.bodySmall
                )
            }
        }
        state.error?.let {
            Text(it.asString(), color = ErrorOnNavy, style = MaterialTheme.typography.bodySmall, modifier = Modifier.padding(top = 8.dp))
        }
    }
}
